import { useEffect, useState } from 'react'
import type { Series } from '../api/types'
import {
  type AxisFormat, type ChartFormat, DEFAULT_SERIES, LINE_WIDTHS, type LineWidth, POINT_SIZES,
  type PointSize, type SeriesFormat,
} from '../lib/chartFormat'

interface Props {
  series: Series[]
  /** A series' line colour and its text colour, as the chart draws it. */
  look: (index: number) => { colour: string; text: string }
  format: ChartFormat | null
  onChange: (format: ChartFormat) => void
  onReset: () => void
  onDone: () => void
}

const EMPTY: ChartFormat = { series: {}, axes: {} }

/** The board's Format panel under a chart: per series the line width,
 *  points and their size; per axis group an optional min and max. Changes
 *  apply as they are made and are saved for this person on this chart. */
export function FormatPanel({ series, look, format, onChange, onReset, onDone }: Props) {
  const current = format ?? EMPTY
  const groups = [...new Set(series.map((s) => s.axis_group))]

  const setSeries = (code: string, patch: Partial<SeriesFormat>) => onChange({
    ...current,
    series: { ...current.series, [code]: { ...(current.series[code] ?? DEFAULT_SERIES), ...patch } },
  })
  const setAxis = (group: string, axis: AxisFormat | null) => {
    const axes = { ...current.axes }
    if (axis === null || (axis.min === null && axis.max === null)) delete axes[group]
    else axes[group] = axis
    onChange({ ...current, axes })
  }

  return (
    <div className="format-panel" role="region" aria-label="Format chart">
      <div className="format-panel-column">
        <div className="format-panel-heading">Series</div>
        {series.map((s, index) => {
          const own = current.series[s.parameter] ?? DEFAULT_SERIES
          return (
            <div key={s.parameter} className="format-panel-row">
              <span className="format-panel-name">
                <span className="format-panel-swatch" style={{ background: look(index).colour }} />
                {s.display_name}
              </span>
              <label>Line
                <select value={own.width}
                        onChange={(e) => setSeries(s.parameter, { width: Number(e.target.value) as LineWidth })}>
                  {LINE_WIDTHS.map((w) => <option key={w} value={w}>{w} px</option>)}
                </select>
              </label>
              <label>
                <input type="checkbox" checked={own.points}
                       onChange={(e) => setSeries(s.parameter, { points: e.target.checked })} />
                Points
              </label>
              <label>Point size
                <select value={own.point_size} disabled={!own.points}
                        onChange={(e) => setSeries(s.parameter, { point_size: Number(e.target.value) as PointSize })}>
                  {POINT_SIZES.map((p) => <option key={p} value={p}>{p} px</option>)}
                </select>
              </label>
            </div>
          )
        })}
      </div>
      <div className="format-panel-column">
        <div className="format-panel-heading">Vertical axes</div>
        {groups.map((group) => {
          const first = series.findIndex((s) => s.axis_group === group)
          const unit = series[first]?.unit
          return (
            <AxisRow key={group} title={`${group}${unit ? ` (${unit})` : ''}`}
                     colour={look(first).text} saved={current.axes[group] ?? null}
                     onCommit={(axis) => setAxis(group, axis)} />
          )
        })}
        <span className="muted format-panel-hint">
          Leave a box empty to keep it automatic. Formatting is saved for you on this chart.
        </span>
      </div>
      <div className="format-panel-actions">
        <button type="button" className="button" onClick={onReset}>Reset formatting</button>
        <button type="button" className="button primary" onClick={onDone}>Done</button>
      </div>
    </div>
  )
}

/** Min and max for one axis group, typed freely and applied only when they
 *  make sense: each a number or empty, and min below max. */
function AxisRow({ title, colour, saved, onCommit }: {
  title: string; colour: string; saved: AxisFormat | null
  onCommit: (axis: AxisFormat | null) => void
}) {
  const [min, setMin] = useState(saved?.min?.toString() ?? '')
  const [max, setMax] = useState(saved?.max?.toString() ?? '')
  const parsed = (text: string) => (text.trim() === '' ? null : Number(text))
  // Follow the saved value only when it says something other than what is
  // typed (a reset, a load): "1." is still 1, and rewriting it would eat
  // the point being typed.
  useEffect(() => {
    if (parsed(min) !== (saved?.min ?? null)) setMin(saved?.min?.toString() ?? '')
    if (parsed(max) !== (saved?.max ?? null)) setMax(saved?.max?.toString() ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved?.min, saved?.max])

  const lo = parsed(min)
  const hi = parsed(max)
  const invalid = (lo !== null && !Number.isFinite(lo)) || (hi !== null && !Number.isFinite(hi))
  const error = !invalid && lo !== null && hi !== null && lo >= hi ? 'Min must be below max.' : null

  const commit = (nextMin: string, nextMax: string) => {
    const a = parsed(nextMin)
    const b = parsed(nextMax)
    if ((a !== null && !Number.isFinite(a)) || (b !== null && !Number.isFinite(b))) return
    if (a !== null && b !== null && a >= b) return
    onCommit({ min: a, max: b })
  }

  return (
    <div className="format-panel-row">
      <span className="format-panel-name" style={{ color: colour }}>{title}</span>
      <label>Min
        <input type="number" step="any" className="mono" value={min} aria-invalid={error !== null}
               onChange={(e) => { setMin(e.target.value); commit(e.target.value, max) }} />
      </label>
      <label>Max
        <input type="number" step="any" className="mono" value={max} aria-invalid={error !== null}
               onChange={(e) => { setMax(e.target.value); commit(min, e.target.value) }} />
      </label>
      {error && <span role="alert" className="format-panel-error">{error}</span>}
    </div>
  )
}
